/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
    Form as FormikForm,
    Masks,
    DatePickerField,
    AutocompleteField,
    InputField,
    CheckboxField,
    TextareaField,
} from "components/common_components/Formik";
import { Field } from "formik";
import * as Yup from "yup";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Grid from "@material-ui/core/Grid";
import { DataSearchDialog } from "components/common_components/Dialogs";
import CustomerEntity from "entities/Customer";
import * as EntityUtils from "entities/utils";
import CustomersContext from "./CustomersContext";
import ProfileCard from "components/common_components/ProfileCard";
import Icon from "components/common_components/Icon";
import Button from "components/common_components/Button";
import Card from "components/common_components/Card";
import Status from "components/common_components/Status";
import { notifyError } from "redux/actions/Notifications";
import { hasElements } from "utils/arrays";
import LoadingPage from "components/common_components/LoadingPage";
import { Tenant } from "utils/tenants";

function Form({ customer, resources, mode, onEdit, ...rest }) {
    const readOnly = mode === "detail";
    const dispatch = useDispatch();
    const { countries } = resources;
    const [initialValues, setInitialValues] = useState(undefined);
    const { user } = useSelector((state) => state.Login);
    const [forms, setForms] = useState([]);
    const [isFormsLoading, setIsFormsLoading] = useState(false);

    const { Visitors, Customers } = useContext(CustomersContext);

    const getForms = () => {
        if (!(customer && customer.id)) return;
        setIsFormsLoading(true);
        Customers.getForms({
            id: customer.id,
            onSuccess: (data) => {
                setForms(data);
                setIsFormsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsFormsLoading(false);
            },
        });
    };

    function init() {
        setInitialValues(EntityUtils.mapEntity(CustomerEntity, customer));
        getForms();
    }

    useEffect(() => {
        init();
    }, [customer]);

    const validationSchema = Yup.object({
        firstName: Yup.string().required("Required field"),
        lastName: Yup.string().required("Required field"),
        homePhone: Yup.string()
            .test("Valid home phone", "Incorrect format. Example 123-456-7890", (value) =>
                value ? /^\d\d\d-\d\d\d-\d\d\d\d/.test(value) : true
            )
            .required("Required field"),
        birthDate: Yup.date().required("Required field"),
        email: Yup.string().notRequired().email("Incorrect email"),
        ssn: Yup.string()
            .test("Valid SSN", "Incorrect format. Example 123-45-6789", (value) => (value ? /^\d\d\d-\d\d-\d\d\d\d/.test(value) : true))
            .notRequired(),
        workPhone: Yup.string()
            .test("Valid work phone", "Incorrect format. Example 123-45-6789", (value) =>
                value ? /^\d\d\d-\d\d\d-\d\d\d\d/.test(value) : true
            )
            .notRequired(),
    });

    function filterVisitorsBuilder(filterValue) {
        return filterValue
            ? {
                  firstName: filterValue,
                  lastName: filterValue,
                  homePhone: filterValue,
              }
            : {};
    }

    const [searchVisitorDialogProps, setSearchVisitorDialogProps] = useState({
        open: false,
        title: "Select a visitor",
        onClose: () => setSearchVisitorDialogProps((prev) => ({ ...prev, open: false })),
        onPrimaryClick: undefined,
        onSecondaryClick: undefined,
        tableProps: {
            defaultOrder: { order: "asc", orderBy: "firstName" },
            keyColumnName: "id",
            selectable: true,
            allowSelectAll: false,
            allowMultiSelect: false,
            dense: false,
            deletable: false,
            onRequestData: Visitors.getListAsync,
            filterBuilder: filterVisitorsBuilder,
        },
    });

    function setCustomerFromVisitor(selectedItems, setValues) {
        const selectedVisitor = selectedItems[0];
        if (selectedVisitor) {
            setValues({
                ssn: selectedVisitor.ssn,
                firstName: selectedVisitor.firstName,
                middleName: selectedVisitor.middleName,
                lastName: selectedVisitor.lastName,
                address: selectedVisitor.address1,
                country: selectedVisitor.country || null,
                city: selectedVisitor.city,
                state: selectedVisitor.state,
                zipCode: selectedVisitor.zipCode,
                homePhone: selectedVisitor.homePhone,
                workPhone: selectedVisitor.workPhone,
                notes: selectedVisitor.notes,
                email: "",
                birthDate: selectedVisitor.dob || null,
                birthPlace: "",
                referredBy: "",
            });
        }
    }

    function searchVisitor({ setValues }) {
        const headCells = [
            { index: 1, id: "firstName", disablePadding: false, label: "First Name" },
            { index: 2, id: "lastName", disablePadding: false, label: "Last Name" },
            { index: 3, id: "homePhone", disablePadding: true, label: "Home Phone" },
            { index: 4, id: "dobString", disablePadding: true, label: "Date of Birth" },
            { index: 5, id: "visitDateString", disablePadding: true, label: "Visit Date" },
        ];
        setSearchVisitorDialogProps((prev) => ({
            ...prev,
            open: true,
            onPrimaryClick: (selectedItems) => setCustomerFromVisitor(selectedItems, setValues),
            tableProps: {
                headCells,
                ...prev.tableProps,
            },
        }));
    }

    return (
        <>
            {initialValues && (
                <FormikForm
                    validateOnChange={false}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    showButtons={!readOnly}
                    {...rest}
                >
                    <Grid container spacing={2} className="customer-form">
                        <Grid item xs={12} md={6} lg={3}>
                            <div className="layout general-info">
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">General Information</FormLabel>
                                    <Field name="photo" type="file">
                                        {({ field, form }) => (
                                            <ProfileCard
                                                {...field}
                                                onChange={(value) => form.setFieldValue(field.name, value)}
                                                readonly={readOnly}
                                            />
                                        )}
                                    </Field>
                                    {mode === "add" && Tenant.getFeatures().visitors.enabled && (
                                        <Field>
                                            {({ _, form }) => (
                                                <Button secondary outline small onClick={() => searchVisitor(form)}>
                                                    <Icon name="search" size="sm" color="secondary" />
                                                    <span>From a Visitor</span>
                                                </Button>
                                            )}
                                        </Field>
                                    )}

                                    <InputField disabled={readOnly} fieldName="ssn" label="SSN" lowercase mask={Masks.ssn} light compact />
                                    <InputField disabled={readOnly} fieldName="firstName" label="First Name *" light compact />
                                    <InputField disabled={readOnly} fieldName="middleName" label="Middle Name" light compact />
                                    <InputField disabled={readOnly} fieldName="lastName" label="Last Name *" light compact />
                                    <DatePickerField disabled={readOnly} fieldName="birthDate" label="Date of Birth *" />
                                    <InputField disabled={readOnly} fieldName="birthPlace" label="Place of Birth " light compact />
                                    {user.isAdmin && <CheckboxField readOnly={readOnly} fieldName="enabled" label="Is Enabled" />}
                                </FormControl>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <div className="layout location">
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Location</FormLabel>
                                    <FormGroup row>
                                        <AutocompleteField
                                            disabled={readOnly}
                                            fieldName="country"
                                            label="Country"
                                            options={countries}
                                            valueKey="code"
                                            labelKey="name"
                                        />
                                        <InputField disabled={readOnly} fieldName="state" label="State" light compact />
                                        <InputField disabled={readOnly} fieldName="city" label="City" light compact />
                                        <InputField disabled={readOnly} fieldName="zipCode" label="Zip Code" light compact />
                                        <InputField disabled={readOnly} fieldName="address" label="Address" light compact />
                                    </FormGroup>
                                </FormControl>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                            <div className="layout contact">
                                <FormControl component="fieldset">
                                    <FormLabel component="legend">Contact and Extra</FormLabel>
                                    <FormGroup row>
                                        <InputField
                                            disabled={readOnly}
                                            fieldName="email"
                                            label={
                                                <>
                                                    {"Email "}
                                                    <Icon
                                                        name="question"
                                                        color="secondary"
                                                        size="xs"
                                                        title="Used for notification purposes."
                                                        style={{ cursor: "help" }}
                                                    />
                                                </>
                                            }
                                            helperText="e.g myname@domain.com"
                                            lowercase
                                            light
                                            compact
                                        />
                                        <InputField
                                            disabled={readOnly}
                                            fieldName="homePhone"
                                            label={
                                                <>
                                                    {"Home Phone * "}
                                                    <Icon
                                                        name="question"
                                                        color="secondary"
                                                        size="xs"
                                                        title="Used for SMS notification purposes."
                                                        style={{ cursor: "help" }}
                                                    />
                                                </>
                                            }
                                            mask={Masks.phoneNumber}
                                            light
                                            compact
                                        />
                                        <InputField
                                            disabled={readOnly}
                                            fieldName="workPhone"
                                            label="Work Phone"
                                            mask={Masks.phoneNumber}
                                            light
                                            compact
                                        />
                                        <InputField disabled={readOnly} fieldName="referredBy" label="Referred By" light compact />
                                        <TextareaField
                                            disabled={readOnly}
                                            fieldName="notes"
                                            label="Notes"
                                            fullWidth
                                            maxLength={500}
                                            rows={10}
                                        />
                                    </FormGroup>
                                </FormControl>
                            </div>
                        </Grid>
                        {readOnly && Tenant.getFeatures().forms.enabled && (
                            <Grid item xs={12} md={6} lg={3}>
                                <div className="layout forms">
                                    <FormLabel component="legend">
                                        Forms <Icon name="beta" color="primary" size="xlg" />
                                    </FormLabel>
                                    {isFormsLoading && <LoadingPage />}
                                    {!isFormsLoading &&
                                        hasElements(forms) &&
                                        forms.map((f) => (
                                            <Card key={f.id}>
                                                <div className="form-info">
                                                    <div className="form-code-status">
                                                        <b>{f.form.code}</b> <Status id={f.status} withIcon />
                                                    </div>
                                                    <div className="form-version">
                                                        <span>{`Version ${f.form.version}`} </span>
                                                        {f.form.isCurrent ? <Status id="active" /> : <Status id="oldVersion" />}
                                                    </div>
                                                </div>
                                                <div className="open-icon" onClick={() => window.open(`/forms/${f.id}`, "_blank")}>
                                                    <Icon name="open-new-tab" color="secondary" size="lg" />
                                                </div>
                                            </Card>
                                        ))}
                                    {!isFormsLoading && !hasElements(forms) && <div className="no-results">No forms created yet!</div>}
                                </div>
                            </Grid>
                        )}
                    </Grid>
                    {readOnly && (
                        <div className="button-group">
                            <Button secondary outline onClick={rest.onCancel}>
                                <Icon name="arrow" size="md" color="secondary" />
                                <span>Back</span>
                            </Button>
                            <Button secondary onClick={onEdit}>
                                <Icon name="pencil" size="md" color="white" />
                                <span>Edit</span>
                            </Button>
                        </div>
                    )}
                </FormikForm>
            )}
            <DataSearchDialog {...searchVisitorDialogProps} />
        </>
    );
}

Form.propTypes = {
    customer: PropTypes.shape(EntityUtils.getEntityTypes(CustomerEntity)),
    resources: PropTypes.shape({
        countries: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                name: PropTypes.string,
            })
        ),
    }),
    mode: PropTypes.oneOf(["add", "detail", "update"]).isRequired,
    onEdit: PropTypes.func,
};

Form.defaultProps = {
    customer: EntityUtils.getEntityDefaults(CustomerEntity),
    resources: {
        countries: [],
    },
    onEdit: () => {},
};

export default Form;
